import { Button, ButtonOutline } from "app/components/ui/button.component";
import { useAuth } from "app/context/auth.context";
import { selectUserState, userActions } from "app/store/slices/user";
import { useAppDispatch, useAppSelector } from "app/store/store";
import type { NextPage } from "next";
import Head from "next/head";
import React, { useCallback, useEffect, useState } from "react";
import styles from "./settings.module.scss";
import {
  useDeletePillowUserV2UsersMeDeezer,
  useDeletePillowUserV2UsersMeSpotify,
} from "services-hooks/hooks";
import { useRouter } from "next/router";
import { Toggle } from "app/components/ui/toggle.component";
import MemoAngleRight from "app/components/icons/AngleRight";
import Link from "next/link";
import { useFeaturesContext } from "app/context/features.context";
import { useCountryContext } from "app/context/country.context";
import { CountrySelector } from "app/components/country-selector/country-selector.component";
import { ExportToPlaylistPopup } from "app/components/export-to-playlist";
import Spotify from "app/components/icons/Spotify";
import MemoDeezer from "app/components/icons/Deezer";
import clsx from "clsx";
import { LocaleSelector } from "app/components/locale-selector/locale-selector.component";
import { useTranslate } from "app/hooks/lang";
import { useGoBackHistory } from "app/hooks/go-back.hook";
import { useNavigatePath } from "app/hooks/navigation.hook";
import { localesMap } from "app/utils/localesMap";
import useMetaDescription from "app/utils/getMetaDescription";
import MemoCheckMarkWide from "app/components/icons/CheckMarkWide";
import { useToast } from "app/components/toast/toast.component";
import { usePlayerContext } from "app/context/player.context";

const Settings: NextPage = () => {
  const navigatePath = useNavigatePath();
  const router = useRouter();
  const t = useTranslate();
  const { message, accountlink, ...rest } = router.query;
  const dispatch = useAppDispatch();
  const { country, setCountry, localeOverride } = useCountryContext();
  const { session } = useAppSelector(selectUserState);
  const {
    appVersion,
    premiumVersion,
    forceAutoplay,
    setForceAutoPlay,
    showAddDevice,
    disableDeleteAccount,
  } = useFeaturesContext();
  const [countrySelectorOpen, setCountrySelectorOpen] = useState(false);
  const [localeSelectorOpen, setLocaleSelectorOpen] = useState(false);
  const [exportToPlaylistPopupOpen, setExportToPlaylistPopupOpen] =
    useState(false);
  const { signOut, refreshUserData } = useAuth();
  const { mutateAsync: unlinkSpotify } = useDeletePillowUserV2UsersMeSpotify();
  const { mutateAsync: unlinkDeezer } = useDeletePillowUserV2UsersMeDeezer();
  const showMessageAfterRedirection = useToast({ content: message });

  const { priceData } = usePlayerContext();

  useGoBackHistory("SETTINGS");
  useEffect(() => {
    if (accountlink) {
      setExportToPlaylistPopupOpen(true);
      router.replace({ pathname: router.pathname, query: rest }, undefined, {
        scroll: false,
      });
    }
  }, [accountlink]);
  const disconnectSpotify = async () => {
    try {
      const response = await unlinkSpotify();
      dispatch(userActions.setUser(response.data.data));
    } catch (e) {
      refreshUserData();
    }
  };

  const disconnectDeezer = async () => {
    try {
      const response = await unlinkDeezer();
      dispatch(userActions.setUser(response.data.data));
    } catch (e) {
      refreshUserData();
    }
  };

  const linkAccount = () => {
    if (session?.user) {
      setExportToPlaylistPopupOpen(true);
    } else {
      navigateLogin();
    }
  };

  const navigateLogin = () => {
    navigatePath("/log-in");
  };

  useEffect(() => {
    refreshUserData();
  }, []);

  const navigatePremium = () => {
    if (!premiumVersion) {
      navigatePath("/get-premium");
    }
  };
  const hasMusicIntegration = !!(
    session?.user?.spotify_connect || session?.user?.deezer_connect
  );

  const disableClick = (e: React.MouseEvent<Element, MouseEvent>) => {
    e.preventDefault();
  };

  const getMetaDescription = useMetaDescription();
  useEffect(() => {
    if (message) {
      showMessageAfterRedirection();
      router.replace({ pathname: router.pathname, query: rest }, undefined, {
        scroll: false,
      });
    }
  }, [message]);

  const closeExportToPlaylistCallback = useCallback(() => {
    setExportToPlaylistPopupOpen(false);
  }, [setExportToPlaylistPopupOpen]);
  const closeCountrySelectorCallback = useCallback(
    () => setCountrySelectorOpen(false),
    [setCountrySelectorOpen]
  );
  const closeLocaleSelectorCallback = useCallback(
    () => setLocaleSelectorOpen(false),
    [setLocaleSelectorOpen]
  );

  return (
    <>
      <div className={styles.container}>
        <Head>
          <title>{t("settings.settings")} - Radioline</title>
          {getMetaDescription(t("settings.meta-description"))}
        </Head>
        <div className={styles.settings_wrapper}>
          <div className={styles.header}>
            {!session?.user && (
              <div data-navigate-row>
                <Button
                  data-navigate-item
                  onClick={() => {
                    navigateLogin();
                  }}
                >
                  {t("create-an-account")} / {t("log-in")}
                </Button>
              </div>
            )}

            {!premiumVersion && !session?.user?.premium && (
              <Link href={"/get-premium"} passHref>
                <a data-navigate-row>
                  <Button accent data-navigate-item>
                    {t("premium.go-premium-for", {
                      var1: `${priceData.amount}${priceData.currency_symbol}`,
                    })}
                  </Button>
                </a>
              </Link>
            )}
          </div>

          <div>
            <section className={styles.section}>
              <div className={styles.section_header}>
                <h2>{t("settings.features")}</h2>
              </div>

              <div className={styles.features}>
                <div
                  data-navigate-row={!premiumVersion && !session?.user?.premium}
                >
                  <div
                    className={styles.feature}
                    tabIndex={0}
                    onClick={() => {
                      if (!session?.user?.premium) {
                        navigatePremium();
                      }
                    }}
                  >
                    <div className={styles.info}>
                      <p className={styles.title}>{t("settings.no-ads")}</p>
                      <p className={styles.description}>
                        {t("settings.no-ads-desc")}
                      </p>
                    </div>
                    <div className={styles.action}>
                      {premiumVersion || !!session?.user?.premium ? (
                        <MemoCheckMarkWide />
                      ) : (
                        <div
                          className={styles.toggle_trigger}
                          tabIndex={0}
                          data-navigate-item
                        >
                          <Toggle
                            disabled={true}
                            checked={premiumVersion || !!session?.user?.premium}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div
                  className={clsx(styles.feature, styles.feature_autoplay)}
                  data-navigate-row
                  onClick={() => {
                    if (!session?.user?.premium) {
                      navigatePremium();
                    } else {
                      setForceAutoPlay(!forceAutoplay);
                    }
                  }}
                >
                  <div className={styles.info}>
                    <p className={styles.title}>{t("autoplay")}</p>
                    <p className={styles.description}>
                      {t("premium.automatically-play-last-radio")}
                    </p>
                  </div>
                  <div className={styles.action}>
                    <div
                      className={styles.toggle_trigger}
                      tabIndex={0}
                      data-navigate-item
                    >
                      <Toggle
                        data-navigate-target
                        checked={forceAutoplay}
                        onChange={(e) =>
                          setForceAutoPlay(
                            (e.target as HTMLInputElement).checked
                          )
                        }
                        disabled={!premiumVersion && !session?.user?.premium}
                      />
                    </div>
                  </div>
                </div>
                <div className={styles.feature} data-navigate-row>
                  <div className={styles.info}>
                    <p className={styles.title}>{t("export-to-playlist")}</p>
                    <p className={styles.description}>
                      {t("settings.export-to-playlist-desc")}
                    </p>
                    {!hasMusicIntegration ? (
                      <div className={styles.music_platforms}>
                        {/* <MemoAppleMusic /> */}
                        <MemoDeezer />
                        <Spotify />
                      </div>
                    ) : (
                      <div className={styles.music_platforms}>
                        <p>{t("settings.connected-to")}</p>
                        {session?.user?.spotify_connect ? (
                          <Spotify />
                        ) : (
                          <MemoDeezer />
                        )}
                      </div>
                    )}
                  </div>
                  <div className={clsx(styles.action, styles.wide)}>
                    {!hasMusicIntegration ? (
                      <ButtonOutline
                        data-navigate-item
                        onClick={() => linkAccount()}
                      >
                        {t("settings.link-account")}
                      </ButtonOutline>
                    ) : (
                      <ButtonOutline
                        data-navigate-item
                        onClick={() => {
                          if (session?.user?.spotify_connect) {
                            disconnectSpotify();
                          } else if (session?.user?.deezer_connect) {
                            disconnectDeezer();
                          }
                        }}
                      >
                        {t("settings.unlink-account")}
                      </ButtonOutline>
                    )}
                  </div>
                </div>
              </div>
            </section>
            <section className={styles.section}>
              <div className={styles.section_header}>
                <h2>{t("settings.my-app")}</h2>
              </div>

              <div className={styles.features}>
                {showAddDevice && (
                  <Link href={"/add-device"} passHref>
                    <a className={styles.feature_link} data-navigate-row>
                      <div className={styles.feature}>
                        <div className={styles.info}>
                          <p className={styles.title}>
                            {t("settings.add-device")}
                          </p>
                        </div>
                        <div className={styles.action}>
                          <button
                            className={styles.toggle_trigger}
                            data-navigate-item
                          >
                            <MemoAngleRight className={styles.angle_right} />
                          </button>
                        </div>
                      </div>
                    </a>
                  </Link>
                )}
                <div
                  className={styles.feature}
                  data-navigate-row
                  onClick={() => {
                    setLocaleSelectorOpen(true);
                  }}
                >
                  <div className={styles.info}>
                    <p className={styles.title}>
                      {t("settings.application-language")}
                    </p>
                    <p className={styles.description}>
                      {localeOverride
                        ? localesMap[localeOverride]
                        : router.locale
                        ? localesMap[router.locale]
                        : router.locale}
                    </p>
                  </div>
                  <div className={styles.action}>
                    <button
                      className={styles.toggle_trigger}
                      data-navigate-item
                    >
                      <MemoAngleRight className={styles.angle_right} />
                    </button>
                  </div>
                </div>
                <div
                  className={styles.feature}
                  onClick={() => setCountrySelectorOpen(true)}
                  data-navigate-row
                >
                  <div className={styles.info}>
                    <p className={styles.title}>
                      {t("settings.homepage-country")}
                    </p>
                  </div>
                  <div className={styles.action}>
                    <button
                      data-navigate-item
                      className={clsx(
                        styles.country_selector,
                        styles.toggle_trigger
                      )}
                    >
                      <img src={country.logo} alt={country.name} />
                    </button>
                  </div>
                </div>
                <Link href={"#"} passHref>
                  <a
                    className={clsx(
                      styles.feature_link,
                      styles.no_hand,
                      styles.app_version_wrapper
                    )}
                    onClick={(e) => disableClick(e)}
                  >
                    <div className={styles.feature}>
                      <div className={styles.info}>
                        <p className={styles.title}>
                          {t("settings.app-version")}
                        </p>
                      </div>
                      <div className={styles.action}>
                        <p className={styles.app_version}>{appVersion}</p>
                      </div>
                    </div>
                  </a>
                </Link>
              </div>
            </section>

            {session?.user && (
              <section className={styles.section}>
                <div className={styles.section_header}>
                  <h2>{t("settings.my-account")}</h2>
                </div>

                <div className={styles.features}>
                  <div
                    className={styles.feature}
                    onClick={() => navigatePath("/change-password")}
                    data-navigate-row
                  >
                    <div className={styles.info}>
                      <p className={styles.title}>
                        {t("auth.change-my-password")}
                      </p>
                    </div>
                    <div className={styles.action}>
                      <button
                        className={styles.toggle_trigger}
                        data-navigate-item
                      >
                        <MemoAngleRight className={styles.angle_right} />
                      </button>
                    </div>
                  </div>

                  {!disableDeleteAccount && (
                    <div
                      className={styles.feature}
                      data-navigate-row
                      onClick={() => navigatePath("/delete-account")}
                    >
                      <div className={styles.info}>
                        <p className={styles.title}>
                          {t("delete-account.button")}
                        </p>
                      </div>
                      <div className={styles.action}>
                        <button
                          className={styles.toggle_trigger}
                          data-navigate-item
                        >
                          <MemoAngleRight className={styles.angle_right} />
                        </button>
                      </div>
                    </div>
                  )}

                  <div
                    className={styles.feature}
                    data-navigate-row
                    onClick={() => {
                      signOut();
                    }}
                  >
                    <div className={styles.info}>
                      <p className={styles.title}>{t("auth.sign-out")}</p>
                    </div>
                    <div className={styles.action}>
                      <button
                        className={styles.toggle_trigger}
                        data-navigate-item
                      >
                        <MemoAngleRight className={styles.angle_right} />
                      </button>
                    </div>
                  </div>
                </div>
              </section>
            )}
            <section className={styles.section}>
              <div className={styles.section_header}>
                <h2>Radioline</h2>
              </div>
              <div className={styles.features}>
                {/* {showInstallApp && (
                  <Link href={"#"} passHref>
                    <a className={styles.feature_link}>
                      <div className={styles.feature}>
                        <div className={styles.info}>
                          <p className={styles.title}>
                            {t("settings.install-app")}
                          </p>
                        </div>
                        <div className={styles.action}>
                          <MemoAngleRight className={styles.angle_right} />
                        </div>
                      </div>
                    </a>
                  </Link>
                )} */}
                <Link href={"/contact-us"} passHref>
                  <a className={styles.feature_link} data-navigate-row>
                    <div className={styles.feature}>
                      <div className={styles.info}>
                        <p className={styles.title}>{t("contact")}</p>
                      </div>
                      <div className={styles.action}>
                        <button
                          className={styles.toggle_trigger}
                          data-navigate-item
                        >
                          <MemoAngleRight className={styles.angle_right} />
                        </button>
                      </div>
                    </div>
                  </a>
                </Link>
                <Link href={"/privacy-policy"} passHref>
                  <a className={styles.feature_link} data-navigate-row>
                    <div className={styles.feature}>
                      <div className={styles.info}>
                        <p className={styles.title}>{t("privacy-policy")}</p>
                      </div>
                      <div className={styles.action}>
                        <button
                          className={styles.toggle_trigger}
                          data-navigate-item
                        >
                          <MemoAngleRight className={styles.angle_right} />
                        </button>
                      </div>
                    </div>
                  </a>
                </Link>
                <Link href={"/terms-of-use"} passHref>
                  <a className={styles.feature_link} data-navigate-row>
                    <div className={styles.feature}>
                      <div className={styles.info}>
                        <p className={styles.title}>{t("terms-of-use")}</p>
                      </div>
                      <div className={styles.action}>
                        <button
                          className={styles.toggle_trigger}
                          data-navigate-item
                        >
                          <MemoAngleRight className={styles.angle_right} />
                        </button>
                      </div>
                    </div>
                  </a>
                </Link>
              </div>
            </section>
          </div>
        </div>
      </div>
      {exportToPlaylistPopupOpen && (
        <ExportToPlaylistPopup onClose={closeExportToPlaylistCallback} />
      )}
      {countrySelectorOpen && (
        <CountrySelector
          onClose={closeCountrySelectorCallback}
          onSelect={(category) => {
            const permalinkSplit = category.permalink!.split("/");
            setCountry({
              countryCode: permalinkSplit[permalinkSplit.length - 1],
              logo: category.logo!,
              name: category.name!,
              permalink: category.permalink!,
              preset: false,
            });
            setCountrySelectorOpen(false);
          }}
        />
      )}
      {localeSelectorOpen && (
        <LocaleSelector onClose={closeLocaleSelectorCallback} />
      )}
      {countrySelectorOpen && (
        <CountrySelector
          onClose={() => setCountrySelectorOpen(false)}
          onSelect={(category) => {
            const permalinkSplit = category.permalink!.split("/");
            setCountry({
              countryCode: permalinkSplit[permalinkSplit.length - 1],
              logo: category.logo!,
              name: category.name!,
              permalink: category.permalink!,
              preset: false,
            });
            setCountrySelectorOpen(false);
          }}
        />
      )}
    </>
  );
};

export default Settings;
